import React, { useEffect, useState } from "react";

const API = "https://smart-bin-backend-zygj.onrender.com";
const token = () => localStorage.getItem("token");

function RhinoDashboard() {
  const [weights, setWeights] = useState([]);
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchMfid, setSearchMfid] = useState("");
  const [traceResult, setTraceResult] = useState(null);
  const [traceError, setTraceError] = useState("");

  const email = localStorage.getItem("email");

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token()}` };
      const [wRes, mRes] = await Promise.all([
        fetch(`${API}/api/weights`, { headers }),
        fetch(`${API}/api/machines`, { headers }),
      ]);
      const [w, m] = await Promise.all([wRes.json(), mRes.json()]);
      setWeights(Array.isArray(w) ? w : []);
      setMachines(Array.isArray(m) ? m : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 20000);
    return () => clearInterval(interval);
  }, []);

  const handleTrace = async () => {
    if (!searchMfid.trim()) return;
    setTraceResult(null);
    setTraceError("");
    try {
      const res = await fetch(`${API}/api/weights/${searchMfid.trim()}`, {
        headers: { Authorization: `Bearer ${token()}` }
      });
      if (!res.ok) { setTraceError("MFID not found."); return; }
      const data = await res.json();
      setTraceResult(data);
    } catch (err) {
      setTraceError("Search failed.");
    }
  };

  const handleLogout = () => { localStorage.clear(); window.location.href = "/"; };

  // inventory by material
  const inventory = weights.reduce((acc, w) => {
    acc[w.materialType] = (acc[w.materialType] || 0) + w.weightKg;
    return acc;
  }, {});

  const totalWeight = weights.reduce((s, w) => s + w.weightKg, 0);
  const totalCO2    = weights.reduce((s, w) => s + w.co2SavedKg, 0);

  if (loading) return <div style={styles.loading}>Loading Rhino Dashboard...</div>;

  return (
    <div style={styles.bg}>
      <div style={styles.container}>

        {/* Header */}
        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>Rhino Machines Portal</h1>
            <p style={styles.sub}>{email} · Partner View</p>
          </div>
          <button style={styles.logoutBtn} onClick={handleLogout}>Logout</button>
        </div>

        {/* KPIs */}
        <div style={styles.kpiGrid}>
          <KPICard label="Total Received"  value={totalWeight.toFixed(1)+" kg"} color="#2E4A3A" />
          <KPICard label="CO₂ Diverted"   value={totalCO2.toFixed(1)+" kg"}    color="#1a6b3c" />
          <KPICard label="Batches"         value={weights.length}               color="#C49A6C" />
          <KPICard label="Active Machines" value={machines.filter(m => m.status === "online").length} color="#2E4A3A" />
        </div>

        {/* Inventory by material */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>📦 Inventory by Material</h2>
          {Object.keys(inventory).length === 0
            ? <p style={styles.empty}>No inventory data yet.</p>
            : Object.entries(inventory).map(([mat, kg]) => (
              <div key={mat} style={styles.invRow}>
                <span style={styles.invMat}>{mat}</span>
                <div style={styles.invBarBg}>
                  <div style={{
                    ...styles.invBar,
                    width: Math.min((kg / totalWeight) * 100, 100) + "%"
                  }} />
                </div>
                <span style={styles.invKg}>{kg.toFixed(1)} kg</span>
              </div>
            ))
          }
        </div>

        {/* MFID Traceability Search */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>🔍 Traceability Search</h2>
          <div style={styles.searchRow}>
            <input
              style={styles.searchInput}
              placeholder="Enter MFID e.g. PET-2026-0001"
              value={searchMfid}
              onChange={e => setSearchMfid(e.target.value)}
              onKeyPress={e => e.key === "Enter" && handleTrace()}
            />
            <button style={styles.searchBtn} onClick={handleTrace}>Search</button>
          </div>
          {traceError && <p style={styles.traceError}>{traceError}</p>}
          {traceResult && (
            <div style={styles.traceCard}>
              <TraceRow label="MFID"         value={traceResult.mfid} />
              <TraceRow label="Material"     value={traceResult.materialType} />
              <TraceRow label="Weight"       value={traceResult.weightKg + " kg"} />
              <TraceRow label="CO₂ Saved"   value={traceResult.co2SavedKg + " kg"} />
              <TraceRow label="Machine"      value={traceResult.machineId} />
              <TraceRow label="YOLO Confidence" value={(traceResult.yoloConfidence * 100).toFixed(0) + "%"} />
              <TraceRow label="Detected As"  value={traceResult.detectedClass} />
              <TraceRow label="Timestamp"    value={new Date(traceResult.timestamp).toLocaleString("en-IN")} />
            </div>
          )}
        </div>

        {/* Recent Batches */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>📋 Recent Batches</h2>
          {weights.length === 0
            ? <p style={styles.empty}>No batches yet.</p>
            : weights.slice(0, 15).map(w => (
              <div key={w._id} style={styles.batchRow}>
                <div>
                  <span style={styles.batchMfid}>{w.mfid}</span>
                  <span style={styles.batchMat}> · {w.materialType}</span>
                </div>
                <div style={styles.batchRight}>
                  <span style={styles.batchKg}>{w.weightKg} kg</span>
                  <span style={styles.batchCo2}>-{w.co2SavedKg} kg CO₂</span>
                </div>
              </div>
            ))
          }
        </div>

      </div>
    </div>
  );
}

function KPICard({ label, value, color }) {
  return (
    <div style={{ ...styles.kpiCard, borderTop: `4px solid ${color}` }}>
      <p style={styles.kpiLabel}>{label}</p>
      <p style={{ ...styles.kpiValue, color }}>{value}</p>
    </div>
  );
}

function TraceRow({ label, value }) {
  return (
    <div style={styles.traceRow}>
      <span style={styles.traceLabel}>{label}</span>
      <span style={styles.traceValue}>{value}</span>
    </div>
  );
}

const styles = {
  bg:           { minHeight: "100vh", background: "#f0f4f0", padding: "20px" },
  container:    { maxWidth: "800px", margin: "0 auto" },
  header:       { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" },
  title:        { margin: 0, fontSize: "24px", color: "#1E3B2C", fontWeight: 700 },
  sub:          { margin: "4px 0 0", color: "#666", fontSize: "13px" },
  logoutBtn:    { padding: "8px 16px", background: "#1E3B2C", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer" },
  kpiGrid:      { display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: "14px", marginBottom: "20px" },
  kpiCard:      { background: "#fff", borderRadius: "12px", padding: "16px", boxShadow: "0 2px 8px rgba(0,0,0,0.07)" },
  kpiLabel:     { margin: "0 0 6px", color: "#666", fontSize: "12px", textTransform: "uppercase" },
  kpiValue:     { margin: 0, fontSize: "26px", fontWeight: 700 },
  section:      { background: "#fff", borderRadius: "12px", padding: "20px", marginBottom: "16px", boxShadow: "0 2px 8px rgba(0,0,0,0.07)" },
  sectionTitle: { margin: "0 0 16px", fontSize: "17px", color: "#1E3B2C", fontWeight: 600 },
  empty:        { color: "#aaa", fontSize: "14px" },
  invRow:       { display: "flex", alignItems: "center", gap: "12px", marginBottom: "10px" },
  invMat:       { width: "60px", fontWeight: 600, color: "#1E3B2C", fontSize: "13px" },
  invBarBg:     { flex: 1, height: "10px", background: "#eee", borderRadius: "5px", overflow: "hidden" },
  invBar:       { height: "100%", background: "#2E4A3A", borderRadius: "5px", transition: "width 0.5s" },
  invKg:        { width: "70px", textAlign: "right", color: "#555", fontSize: "13px", fontWeight: 600 },
  searchRow:    { display: "flex", gap: "10px", marginBottom: "14px" },
  searchInput:  { flex: 1, padding: "10px 14px", border: "1px solid #ddd", borderRadius: "8px", fontSize: "14px" },
  searchBtn:    { padding: "10px 20px", background: "#2E4A3A", color: "#fff", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: 600 },
  traceError:   { color: "#B43232", fontSize: "14px" },
  traceCard:    { border: "1px solid #e0ece4", borderRadius: "10px", overflow: "hidden" },
  traceRow:     { display: "flex", justifyContent: "space-between", padding: "10px 14px", borderBottom: "1px solid #f0f0f0" },
  traceLabel:   { color: "#666", fontSize: "13px" },
  traceValue:   { fontWeight: 600, color: "#1E3B2C", fontSize: "13px" },
  batchRow:     { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: "1px solid #f5f5f5" },
  batchMfid:    { fontWeight: 600, color: "#1E3B2C", fontSize: "14px" },
  batchMat:     { color: "#666", fontSize: "13px" },
  batchRight:   { display: "flex", gap: "12px" },
  batchKg:      { fontWeight: 600, color: "#1E3B2C" },
  batchCo2:     { color: "#1a6b3c", fontSize: "13px" },
  loading:      { display: "flex", justifyContent: "center", alignItems: "center", height: "100vh", color: "#1E3B2C" },
};

export default RhinoDashboard;